"use client";

import React from "react";
import Section from "@/components/Sections/Section";
import Reveal from "@/components/Sections/Reveal";
import {
  ArrowLeft,
  Clock,
  Facebook,
  Twitter,
  Link as LinkIcon,
} from "lucide-react";
import Link from "next/link";
import Image from "next/image";
import appendImageUrlFromAPI from "@/utils/appendImageUrlFromAPI";
import SameNews from "../components/SameNews";

export default function TinTucDetailClient({
  news,
  locale,
  dict,
  footerInfo,
}: {
  news: any;
  locale: string;
  dict: any;
  footerInfo: any;
}) {
  const handleShareClick = (platform: "fb" | "tw" | "copy") => {
    const url = typeof window !== "undefined" ? window.location.href : "";
    if (platform === "fb") {
      window.open(
        `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
        "_blank",
      );
    } else if (platform === "tw") {
      window.open(
        `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}`,
        "_blank",
      );
    } else if (platform === "copy") {
      navigator.clipboard.writeText(url);
      alert("Đã sao chép liên kết vào bộ nhớ tạm!");
    }
  };

  return (
    <div className="min-h-screen bg-[#06191e] font-sans text-white">
      <main className="pt-[80px] md:pt-[90px] pb-16">
        <Section>
          <Reveal>
            <Link
              href={`/${locale}/tin-tuc`}
              className="inline-flex items-center gap-2 text-[#DBB653] font-bold mb-8 hover:gap-3 transition-all font-montserrat"
            >
              <ArrowLeft className="w-4 h-4" /> {dict.back_to_list}
            </Link>

            <div className="mb-10">
              <div className="inline-block px-3.5 py-1.5 rounded-xl text-xs font-bold bg-white/5 text-[#DBB653] mb-6 border border-white/10">
                {news.loai_tin_tuc ||
                  dict.categories?.company ||
                  "CCN Tây Bắc Đức Hòa Đông"}
              </div>
              <h1 className="text-2xl md:text-4xl font-black text-white leading-tight mb-6 uppercase tracking-wide font-montserrat">
                {news.title}
              </h1>

              <div className="flex flex-wrap items-center gap-6 text-white/40 font-medium border-b border-white/10 pb-8">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-[#DBB653]" />{" "}
                  {news.date_created
                    ? new Date(news.date_created).toLocaleDateString("vi-VN", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                      })
                    : ""}
                </div>
                <div className="w-1.5 h-1.5 rounded-full bg-[#DBB653]/40"></div>
                <div className="font-montserrat">
                  {dict.editorial_board || "Ban biên tập"}
                </div>
              </div>
            </div>
          </Reveal>

          <div className="grid lg:grid-cols-[1fr_300px] gap-12">
            <Reveal delay={300}>
              {news.picture && (
                <div className="relative aspect-[21/9] rounded-2xl overflow-hidden mb-12 bg-white/5 shadow-2xl border border-white/10">
                  <Image
                    src={appendImageUrlFromAPI({
                      src: news.picture,
                      size: "2k",
                    })}
                    alt={news.title}
                    fill
                    sizes="(max-width: 1024px) 100vw, 1200px"
                    priority
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                </div>
              )}

              <article className="bg-white rounded-2xl p-6 md:p-10 shadow-xl border border-slate-100 prose prose-slate prose-lg max-w-none prose-headings:text-slate-900 prose-headings:font-bold prose-headings:font-montserrat prose-p:text-slate-700 prose-p:leading-relaxed prose-li:text-slate-600 prose-strong:text-slate-900 prose-blockquote:border-[#DBB653] prose-blockquote:bg-slate-50 prose-blockquote:py-1 prose-blockquote:rounded-r-xl">
                <div
                  className="webview space-y-4 font-medium text-slate-800 text-justify font-montserrat"
                  dangerouslySetInnerHTML={{ __html: news.content }}
                />
              </article>

              <div className="mt-16 pt-8 border-t border-white/10 flex flex-wrap gap-4 items-center justify-between">
                <div className="flex flex-wrap gap-2">
                  {[
                    "TayBacDucHoaDong",
                    "CCN",
                    "IndustrialCluster",
                    "ZenGroup",
                    "LongAn",
                  ].map((tag) => (
                    <span
                      key={tag}
                      className="px-4 py-1.5 bg-white/5 text-white/60 rounded-full text-sm font-medium hover:bg-[#DBB653]/15 hover:text-[#DBB653] border border-white/10 hover:border-[#DBB653]/30 transition-all cursor-pointer font-montserrat"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>

                <div className="flex items-center gap-3">
                  <span className="text-sm font-bold text-white/40 uppercase tracking-wider font-montserrat">
                    {dict.share || "Chia sẻ"}:
                  </span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleShareClick("fb")}
                      className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white hover:bg-[#DBB653] hover:text-[#0b3848] hover:border-transparent transition-all shadow-sm"
                      title="Chia sẻ Facebook"
                    >
                      <Facebook className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleShareClick("tw")}
                      className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white hover:bg-[#DBB653] hover:text-[#0b3848] hover:border-transparent transition-all shadow-sm"
                      title="Chia sẻ Twitter"
                    >
                      <Twitter className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleShareClick("copy")}
                      className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white hover:bg-[#DBB653] hover:text-[#0b3848] hover:border-transparent transition-all shadow-sm"
                      title="Sao chép liên kết"
                    >
                      <LinkIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </Reveal>

            <aside className="hidden lg:block">
              <div className="sticky top-32">
                <SameNews without={news._id} />
              </div>
            </aside>
          </div>
        </Section>
      </main>
    </div>
  );
}
